import { useMemo, useState } from 'react';
import { Box, Checkbox, ListItemText, MenuItem, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import {
  ActionIconButton,
  CustomAutoComplete,
  CustomSelect,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  HorizontalForm,
  NoDataSkeleton,
  PaginationTable,
  RowTableCell,
  TableBodySkeleton,
} from '../../../common/components';
import { useSupplierLedgerProviders } from '../../../hooks/useSupplierLedger';
import { INVOICE_DISPLAY_STATUS_LABELS, compactFormSx, tabPanelSx } from '../constants/supplierLedgerUiConstants';
import { formatAmount } from '../utils/supplierLedgerFormatters';
import useClientPagination from '../hooks/useClientPagination';
import ProviderDetailDrawer from './ProviderDetailDrawer';

const dateFilterSx = {
  width: 154,
  flex: '0 0 154px',
  mt: 0,
  '& .MuiOutlinedInput-root': {
    height: 56,
  },
  '& .MuiInputBase-input[type="date"]': {
    fontSize: 16,
    paddingLeft: '12px',
    paddingRight: '6px',
    '&::-webkit-calendar-picker-indicator': {
      marginLeft: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
};

const statusFilterSx = {
  width: 216,
  flex: '0 0 216px',
  mt: 0,
};

const ProvidersTab = () => {
  const [filters, setFilters] = useState({
    provider: '',
    with_pending_balance: '',
    invoice_start_date: '',
    invoice_end_date: '',
    due_start_date: '',
    due_end_date: '',
    statuses: [],
  });
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const statusOptions = useMemo(
    () => Object.entries(INVOICE_DISPLAY_STATUS_LABELS).map(([value, label]) => ({ value, label })),
    [],
  );
  const statusLabelsByValue = useMemo(
    () => Object.fromEntries(statusOptions.map((option) => [option.value, option.label])),
    [statusOptions],
  );
  const apiFilters = useMemo(() => {
    const payload = Object.fromEntries(Object.entries(filters).filter(([, value]) => (
      Array.isArray(value) ? value.length > 0 : value !== ''
    )));
    if (Array.isArray(payload.statuses)) {
      payload.statuses = payload.statuses.join(',');
    }
    return payload;
  }, [filters]);
  const { data: providers = [], isLoading } = useSupplierLedgerProviders(apiFilters);
  const visibleProviders = useMemo(() => {
    const hasAmount = (provider) => (
      Math.abs(Number(provider.total_invoiced_ars || 0)) > 0
      || Math.abs(Number(provider.paid_amount_ars || 0)) > 0
      || Math.abs(Number(provider.pending_amount_ars || 0)) > 0
      || Math.abs(Number(provider.ledger_balance_ars || 0)) > 0
    );

    const compareAmountDesc = (left, right, field) => (
      Number(right[field] || 0) - Number(left[field] || 0)
    );

    return providers
      .filter(hasAmount)
      .sort((left, right) => (
        compareAmountDesc(left, right, 'pending_amount_ars')
        || compareAmountDesc(left, right, 'ledger_balance_ars')
        || compareAmountDesc(left, right, 'total_invoiced_ars')
        || String(left.provider_name || '').localeCompare(String(right.provider_name || ''))
      ));
  }, [providers]);
  const providerPagination = useClientPagination(visibleProviders);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  const handleStatusFilterChange = (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      statuses: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  return (
    <Box sx={tabPanelSx}>
      <HorizontalForm sx={{ ...compactFormSx, alignItems: 'flex-start' }}>
        <CustomTextField label="Proveedor o CUIT" name="provider" value={filters.provider} onChange={handleChange} sx={{ width: 320, flex: '0 0 320px', mt: 0 }} />
        <CustomAutoComplete label="Saldo" name="with_pending_balance" value={filters.with_pending_balance} onChange={handleChange} sx={{ width: 198, flex: '0 0 198px', mt: 0 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="true">Con saldo</MenuItem>
          <MenuItem value="false">Sin saldo</MenuItem>
        </CustomAutoComplete>
        <CustomTextField label="Factura desde" type="date" name="invoice_start_date" value={filters.invoice_start_date} onChange={handleChange} shrinkLabel sx={dateFilterSx} />
        <CustomTextField label="Factura hasta" type="date" name="invoice_end_date" value={filters.invoice_end_date} onChange={handleChange} shrinkLabel sx={dateFilterSx} />
        <CustomTextField label="Venc. desde" type="date" name="due_start_date" value={filters.due_start_date} onChange={handleChange} shrinkLabel sx={dateFilterSx} />
        <CustomTextField label="Venc. hasta" type="date" name="due_end_date" value={filters.due_end_date} onChange={handleChange} shrinkLabel sx={dateFilterSx} />
        <CustomSelect
          label="Estados factura"
          name="statuses"
          value={filters.statuses}
          onChange={handleStatusFilterChange}
          multiple
          displayEmpty
          renderValue={(selected) => {
            if (!selected.length) return 'Todos';
            return selected.map((status) => statusLabelsByValue[status] || status).join(', ');
          }}
          sx={statusFilterSx}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox size="small" checked={filters.statuses.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </CustomSelect>
      </HorizontalForm>
      <GeneralTableBox>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <HeadTableCell>Proveedor</HeadTableCell>
              <HeadTableCell>CUIT</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Facturado ARS</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Pagado ARS</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Pendiente ARS</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Saldo ARS</HeadTableCell>
              <HeadTableCell>Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodySkeleton />
            ) : visibleProviders.length ? providerPagination.paginatedRows.map((provider) => (
              <TableRow key={provider.uuid_legal_entity} hover>
                <RowTableCell>{provider.provider_name}</RowTableCell>
                <RowTableCell>{provider.cuit || '-'}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(provider.total_invoiced_ars)}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(provider.paid_amount_ars)}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(provider.pending_amount_ars)}</RowTableCell>
                <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(provider.ledger_balance_ars)}</RowTableCell>
                <RowTableCell>
                  <Tooltip title="Ver detalle">
                    <span>
                      <ActionIconButton variant="view" onClick={() => setSelectedProviderId(provider.uuid_legal_entity)} />
                    </span>
                  </Tooltip>
                </RowTableCell>
              </TableRow>
            )) : (
              <NoDataSkeleton colSpan={7} message="No hay proveedores con movimientos de cuenta corriente." />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
      {!!visibleProviders.length && (
        <PaginationTable
          page={providerPagination.page}
          totalPages={providerPagination.totalPages}
          totalCount={providerPagination.totalCount}
          rowsPerPage={providerPagination.rowsPerPage}
          onPageChange={providerPagination.handlePageChange}
          onRowsPerPageChange={providerPagination.handleRowsPerPageChange}
        />
      )}
      <ProviderDetailDrawer providerId={selectedProviderId} onClose={() => setSelectedProviderId('')} />
    </Box>
  );
};

export default ProvidersTab;
